import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Landing.css';

// Every feature description below is written from the real app screens.
const FEATURES = [
  {
    img: '/screen-order.png', tag: 'Storefront',
    title: 'Catalog & 1-click orders',
    desc: "A searchable storefront of your cakes — by theme, flavour, event or shape. Tap any cake for its weights, price and popularity, then add it straight to an enquiry in a single click.",
  },
  {
    img: '/screen-custom.png', tag: 'Customise',
    title: 'Custom cake designer',
    desc: "Customers design their own cake: browse reference designs, then choose flavour, cover, filling, frosting and sponge, mark it eggless or gluten-free, set the quantity and go multi-tier.",
  },
  {
    img: '/screen-imagestudio.png', tag: 'AI · Design',
    title: 'AI Image Studio',
    desc: "Open a reference photo, draw and annotate on it, then type plain-English edits like “make the costume darker, not the skin” — and save, share or print the result.",
  },
  {
    img: '/screen-chat.png', tag: 'Communicate',
    title: 'In-app chat',
    desc: "Message customers and your team in real time — share images, search the whole history by phrase or date, and see delivery & read receipts, right where the order lives.",
  },
  {
    img: '/screen-analytics.png', tag: 'Insights',
    title: 'Owner analytics',
    desc: "Track order count and cake weight over time — a single year or year-on-year comparison — plus your top buyers and best-selling flavours. PIN-protected for owners.",
  },
  {
    img: '/screen-organize.png', tag: 'Logistics',
    title: 'Delivery route planner',
    desc: "Plan multi-stop delivery routes on a live map with per-leg distances, drive times and a schedule — so every cake leaves and arrives on time.",
  },
  {
    img: '/screen-housekeeping.png', tag: 'Data',
    title: 'Data house-keeping',
    desc: "A power console: connect your database, files, Google Drive or storage buckets, search across every field, then edit, export, print or clean up records in bulk.",
  },
  {
    img: '/screen-jobwizard.png', tag: 'Automation',
    title: 'Job Wizard automation',
    desc: "Schedule recurring backup, cleanup and sync jobs — hourly, daily or weekly — then watch their last/next run, record counts and status from one table.",
  },
];

const ROLES = [
  { emoji: '👑', role: 'Owners', desc: 'Analytics, enquiries and the full pipeline — plus the power tools no one else sees.' },
  { emoji: '🧑‍🍳', role: 'Admins & Bakers', desc: 'Run the catalog, custom orders, chat and day-to-day production.' },
  { emoji: '🚚', role: 'Delivery', desc: 'Optimised multi-stop routes and dispatch, straight to a map.' },
  { emoji: '🛍️', role: 'Customers', desc: 'Browse, customise, order and chat — a beautiful experience end to end.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const [legal, setLegal] = useState(null);
  const [zoom, setZoom] = useState(null); // { src, alt } for the lightbox
  const year = new Date().getFullYear();
  const goLogin = () => navigate('/login');
  const goSignup = () => navigate('/signup');

  return (
    <div className="pl">
      <header className="pl-nav">
        <div className="pl-wrap pl-navrow">
          <a className="pl-brand" href="#top">
            <img src="/pcakelogo.png" alt="" className="pl-logo" />
            <span>Craft Your Crave<small>Bakery business, one app</small></span>
          </a>
          <nav className="pl-links">
            <a href="#features">Features</a>
            <a href="#roles">For your team</a>
            <a href="#cta">Get started</a>
          </nav>
          <button className="pl-btn pl-btn-sm" onClick={goLogin}>Sign in</button>
        </div>
      </header>

      {/* Hero */}
      <section className="pl-hero" id="top">
        <div className="pl-wrap pl-herogrid">
          <div className="pl-herotext">
            <span className="pl-eyebrow">🎂 The all-in-one cake-business app</span>
            <h1 className="pl-title">Run your whole cake business <span className="pl-hl">in one app.</span></h1>
            <p className="pl-lead">
              From a customer's first tap to the cake at their door — catalog, custom orders, an AI image
              studio, chat, delivery routing, analytics and automation, all in one place.
            </p>
            <div className="pl-cta">
              <button className="pl-btn pl-btn-lg" onClick={goSignup}>Get started free →</button>
              <button className="pl-btn pl-ghost pl-btn-lg" onClick={goLogin}>Sign in</button>
            </div>
            <div className="pl-trust">
              <div><b>8</b><span>tools in one</span></div>
              <div><b>4</b><span>team roles</span></div>
              <div><b>1</b><span>place for it all</span></div>
            </div>
          </div>
          <div className="pl-heroshot">
            <div className="pl-browser">
              <div className="pl-browser-bar"><span></span><span></span><span></span></div>
              <img src="/screen-home.png" alt="Craft Your Crave — storefront homepage" loading="eager" />
            </div>
            <div className="pl-glow" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="pl-marquee">
        <div className="pl-marquee-track">
          {['Catalog & Orders','Custom Designer','AI Image Studio','In-App Chat','Owner Analytics','Delivery Routing','Data House-Keeping','Job Automation','Catalog & Orders','Custom Designer','AI Image Studio','In-App Chat','Owner Analytics','Delivery Routing','Data House-Keeping','Job Automation'].map((t,i)=>(
            <span key={i}>● {t}</span>
          ))}
        </div>
      </div>

      {/* Features — real screens */}
      <section className="pl-section" id="features">
        <div className="pl-wrap">
          <div className="pl-head">
            <div className="pl-kicker">Real screens, real workflow</div>
            <h2>Everything a cake business needs</h2>
            <p>Not a mock-up — this is the actual app. Eight tools that take you from enquiry to doorstep.</p>
          </div>
          <div className="pl-grid">
            {FEATURES.map(f => (
              <article className="pl-card" key={f.title}>
                <button type="button" className="pl-shot" onClick={() => setZoom({ src: f.img, alt: `${f.title} screen` })} aria-label={`Zoom ${f.title} screen`}>
                  <img src={f.img} alt={`${f.title} screen`} loading="lazy" />
                </button>
                <div className="pl-card-body">
                  <span className="pl-tag">{f.tag}</span>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Spotlight — Image Studio */}
      <section className="pl-section pl-soft">
        <div className="pl-wrap pl-spot">
          <button type="button" className="pl-spot-img" onClick={() => setZoom({ src: '/screen-imagestudio.png', alt: 'AI Image Studio editing a cake design' })} aria-label="Zoom Image Studio screen">
            <img src="/screen-imagestudio.png" alt="AI Image Studio editing a cake design" loading="lazy" />
          </button>
          <div className="pl-spot-text">
            <div className="pl-kicker">Why teams love it</div>
            <h2>An AI studio built for cake art</h2>
            <p>Most bakeries juggle WhatsApp, a photo editor and a notebook. Craft Your Crave folds it into one: mark up a reference image and just describe the change in words — the studio does the rest.</p>
            <ul className="pl-ticks">
              <li>Draw, annotate &amp; layer reference photos</li>
              <li>Natural-language edits — no design skills needed</li>
              <li>Save to the order, share to chat, or print the spec</li>
            </ul>
            <button className="pl-btn pl-btn-lg" onClick={goSignup}>Try it free →</button>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="pl-section" id="roles">
        <div className="pl-wrap">
          <div className="pl-head">
            <div className="pl-kicker">For your whole team</div>
            <h2>Every role, the right screens</h2>
            <p>One platform that adapts to who's signed in — from the owner to the customer.</p>
          </div>
          <div className="pl-roles">
            {ROLES.map(r => (
              <div className="pl-role" key={r.role}>
                <div className="pl-role-emoji">{r.emoji}</div>
                <h3>{r.role}</h3>
                <p>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pl-cta-band" id="cta">
        <div className="pl-wrap pl-cta-inner">
          <h2>Ready to run your bakery the smart way?</h2>
          <p>Create an account in seconds — or sign in to pick up where you left off.</p>
          <div className="pl-cta">
            <button className="pl-btn pl-btn-lg pl-btn-light" onClick={goSignup}>Get started free →</button>
            <button className="pl-btn pl-ghost pl-ghost-light pl-btn-lg" onClick={goLogin}>Sign in</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pl-foot">
        <div className="pl-wrap pl-footrow">
          <a className="pl-brand pl-brand-foot" href="#top">
            <img src="/pcakelogo.png" alt="" className="pl-logo" />
            <span>Craft Your Crave<small>by Priyas</small></span>
          </a>
          <div className="pl-footlinks">
            <a href="#features">Features</a>
            <a href="#roles">For your team</a>
            <button onClick={() => setLegal('terms')}>Terms</button>
            <button onClick={() => setLegal('privacy')}>Privacy</button>
          </div>
        </div>
        <div className="pl-copy">
          © {year} Craft Your Crave by Priyas — a <span>My Creative World (MCW)</span> product. Built with love. All rights reserved.
        </div>
      </footer>

      {/* Zoom lightbox */}
      {zoom && (
        <div className="pl-zoom" onClick={() => setZoom(null)} role="dialog" aria-modal="true">
          <button className="pl-zoom-close" onClick={() => setZoom(null)} aria-label="Close">×</button>
          <img src={zoom.src} alt={zoom.alt} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Legal modal */}
      {legal && (
        <div className="pl-modal" onClick={() => setLegal(null)}>
          <div className="pl-modal-card" onClick={e => e.stopPropagation()}>
            <div className="pl-modal-head">
              <h3>{legal === 'terms' ? 'Terms of Service' : 'Privacy Policy'}</h3>
              <button onClick={() => setLegal(null)} aria-label="Close">×</button>
            </div>
            <div className="pl-modal-body">
              {legal === 'terms' ? (
                <>
                  <p>Craft Your Crave is a cake-business management app operated by <strong>My Creative World (MCW)</strong>.</p>
                  <p><strong>Use.</strong> Accounts are for managing or ordering from a bakery business. Keep your login safe; you're responsible for activity on your account.</p>
                  <p><strong>Content.</strong> Images and order details you upload remain yours; you grant us permission to display them inside the app to run your workflow.</p>
                  <p><strong>As-is.</strong> The app is provided without warranty; we're not liable for losses arising from its use. Be kind, don't misuse it.</p>
                  <p>Questions? <a href="mailto:admin@mycreativeworld.app">admin@mycreativeworld.app</a></p>
                </>
              ) : (
                <>
                  <p><strong>My Creative World (MCW)</strong> respects your privacy.</p>
                  <p><strong>What we keep.</strong> Your account details and the business/order data you create, used only to run the app for you and your team.</p>
                  <p><strong>We never sell your data.</strong> It's shared only with the providers needed to operate the app (e.g. Google Firebase).</p>
                  <p><strong>Your control.</strong> Ask us anytime to export or delete your data.</p>
                  <p>Contact: <a href="mailto:admin@mycreativeworld.app">admin@mycreativeworld.app</a></p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
